/**
 * LocalStorage Sync Service
 * 
 * Automatically saves all important user data to localStorage
 * with automatic 7-day clearing via CacheService
 * 
 * FEATURES:
 * ✅ Saves user profile, preferences, progress
 * ✅ Syncs roadmap progress and article progress
 * ✅ Syncs subscription status
 * ✅ Auto-clears data older than 7 days
 * ✅ Fallback when Supabase is unavailable
 */

import { CacheService } from './cacheService';

export interface LocalUserProfile {
  email: string;
  displayName?: string;
  avatar?: string;
  theme?: string;
  language?: string;
  lastSynced: number;
}

export interface LocalRoadmapProgress {
  roadmapId: string;
  status: string;
  progressPercentage: number;
  timesViewed: number;
  totalMinutesStudied: number;
  lastAccessed: number;
  lastSynced: number;
}

export interface LocalArticleProgress {
  articleId: string;
  completed: boolean;
  progress: number;
  lastViewed: number;
  lastSynced: number;
}

export class LocalStorageSyncService {
  /**
   * Save user profile to localStorage
   */
  static saveUserProfile(profile: LocalUserProfile): void {
    try {
      const key = CacheService.generateKey('user_profile', profile.email);
      CacheService.set(key, profile, 1000 * 60 * 60 * 24 * 7, 'user');
      console.log('✅ User profile saved to localStorage');
    } catch (error) {
      console.error('Failed to save user profile:', error);
    }
  }

  /**
   * Get user profile from localStorage
   */
  static getUserProfile(email: string): LocalUserProfile | null {
    try {
      const key = CacheService.generateKey('user_profile', email);
      return CacheService.get<LocalUserProfile>(key);
    } catch (error) {
      console.error('Failed to get user profile:', error);
      return null;
    }
  }

  /**
   * Save roadmap progress to localStorage
   */
  static saveRoadmapProgress(progress: LocalRoadmapProgress): void {
    try {
      const key = CacheService.generateKey('roadmap_progress', progress.roadmapId);
      CacheService.set(key, progress, 1000 * 60 * 60 * 24 * 7, 'progress');
      console.log(`✅ Roadmap progress saved: ${progress.roadmapId}`);
    } catch (error) {
      console.error('Failed to save roadmap progress:', error);
    }
  }

  /**
   * Get roadmap progress from localStorage
   */
  static getRoadmapProgress(roadmapId: string): LocalRoadmapProgress | null {
    try {
      const key = CacheService.generateKey('roadmap_progress', roadmapId);
      return CacheService.get<LocalRoadmapProgress>(key);
    } catch (error) {
      console.error('Failed to get roadmap progress:', error);
      return null;
    }
  }

  /**
   * Save all roadmap progress at once
   */
  static saveAllRoadmapProgress(progressList: LocalRoadmapProgress[]): void {
    progressList.forEach(progress => this.saveRoadmapProgress(progress));
    console.log(`✅ Saved ${progressList.length} roadmap progress entries`);
  }

  /**
   * Get all roadmap progress
   */
  static getAllRoadmapProgress(): LocalRoadmapProgress[] {
    const allProgress: LocalRoadmapProgress[] = [];
    const prefix = CacheService.generateKey('roadmap_progress');
    
    Object.keys(localStorage).forEach(key => {
      if (key.includes(prefix)) {
        try {
          const raw = localStorage.getItem(key);
          if (raw) {
            const entry = JSON.parse(raw);
            if (entry.data && entry.data.roadmapId) {
              allProgress.push(entry.data as LocalRoadmapProgress);
            }
          }
        } catch (error) {
          console.error(`Failed to parse progress entry ${key}:`, error);
        }
      }
    });
    
    return allProgress;
  }

  /**
   * Save article progress to localStorage
   */
  static saveArticleProgress(progress: LocalArticleProgress): void {
    try {
      const key = CacheService.generateKey('article_progress', progress.articleId);
      CacheService.set(key, progress, 1000 * 60 * 60 * 24 * 7, 'progress');
      console.log(`✅ Article progress saved: ${progress.articleId}`);
    } catch (error) {
      console.error('Failed to save article progress:', error);
    }
  }

  /**
   * Get article progress from localStorage
   */
  static getArticleProgress(articleId: string): LocalArticleProgress | null {
    try {
      const key = CacheService.generateKey('article_progress', articleId);
      return CacheService.get<LocalArticleProgress>(key);
    } catch (error) {
      console.error('Failed to get article progress:', error);
      return null;
    }
  }

  /**
   * Save all article progress at once
   */
  static saveAllArticleProgress(progressList: LocalArticleProgress[]): void {
    progressList.forEach(progress => this.saveArticleProgress(progress));
    console.log(`✅ Saved ${progressList.length} article progress entries`);
  }

  /**
   * Get all article progress
   */
  static getAllArticleProgress(): LocalArticleProgress[] {
    const allProgress: LocalArticleProgress[] = [];
    const prefix = CacheService.generateKey('article_progress');
    
    Object.keys(localStorage).forEach(key => {
      if (key.includes(prefix)) {
        try {
          const raw = localStorage.getItem(key);
          if (raw) {
            const entry = JSON.parse(raw);
            if (entry.data && entry.data.articleId) {
              allProgress.push(entry.data as LocalArticleProgress);
            }
          }
        } catch (error) {
          console.error(`Failed to parse progress entry ${key}:`, error);
        }
      }
    });
    
    return allProgress;
  }

  /**
   * Save user preferences to localStorage
   */
  static savePreferences(email: string, preferences: Record<string, any>): void {
    try {
      const key = CacheService.generateKey('user_preferences', email);
      CacheService.set(key, preferences, 1000 * 60 * 60 * 24 * 7, 'profile');
      console.log('✅ User preferences saved to localStorage');
    } catch (error) {
      console.error('Failed to save preferences:', error);
    }
  }

  /**
   * Get user preferences from localStorage
   */
  static getPreferences(email: string): Record<string, any> | null {
    try {
      const key = CacheService.generateKey('user_preferences', email);
      return CacheService.get<Record<string, any>>(key);
    } catch (error) {
      console.error('Failed to get preferences:', error);
      return null;
    }
  }

  /**
   * Save subscription status to localStorage
   */
  static saveSubscriptionStatus(email: string, status: {
    isPro: boolean;
    expiresAt?: number;
    lastSynced: number;
  }): void {
    try {
      const key = CacheService.generateKey('subscription_status', email);
      CacheService.set(key, status, 1000 * 60 * 5, 'subscription'); // 5 min TTL
      console.log('✅ Subscription status saved to localStorage');
    } catch (error) {
      console.error('Failed to save subscription status:', error);
    }
  }

  /**
   * Get subscription status from localStorage
   */
  static getSubscriptionStatus(email: string): { isPro: boolean; expiresAt?: number; lastSynced: number } | null {
    try {
      const key = CacheService.generateKey('subscription_status', email);
      return CacheService.get<{ isPro: boolean; expiresAt?: number; lastSynced: number }>(key);
    } catch (error) {
      console.error('Failed to get subscription status:', error);
      return null;
    }
  }

  /**
   * Clear all user data from localStorage
   * (Respects 7-day auto-clear managed by CacheService)
   */
  static clearAllUserData(email: string): void {
    try {
      CacheService.invalidateCategories('user', 'progress', 'profile', 'subscription');
      console.log('✅ All user data cleared from localStorage');
    } catch (error) {
      console.error('Failed to clear user data:', error);
    }
  }

  /**
   * Get comprehensive cache statistics
   */
  static getCacheStats() {
    return CacheService.getStats();
  }

  /**
   * Check if localStorage has any recent data for offline mode
   */
  static hasOfflineData(email: string): boolean {
    const profile = this.getUserProfile(email);
    const roadmaps = this.getAllRoadmapProgress();
    const articles = this.getAllArticleProgress();
    
    return !!(profile || roadmaps.length > 0 || articles.length > 0);
  }

  /**
   * Get all cached data for a user (useful for debugging)
   */
  static getAllUserData(email: string) {
    return {
      profile: this.getUserProfile(email),
      roadmaps: this.getAllRoadmapProgress(),
      articles: this.getAllArticleProgress(),
      preferences: this.getPreferences(email),
      subscription: this.getSubscriptionStatus(email),
      cacheStats: this.getCacheStats(),
    };
  }
}
